import styled from 'styled-components'

import {
  TypeStyle,
  fromMd,
  screenReaderOnly,
  timingSlow,
  timingSlower,
  untilMd,
  zIndexModal,
} from '@syconium/little-miss-figgy'

import { NextDialog, NextDialogBackdrop } from '../../../app/_components/dialogs/NextDialog.client'

export const BACKDROP_TIMING = timingSlower
export const DIALOG_TIMING = timingSlow

export const Dialog = styled(NextDialog)`
  background: ${o => o.theme.color.fill.subtle};
  border-radius: 0;
  border-top: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  height: 100%;
  margin: 0;
  max-height: 100%;
  max-width: 100%;
  min-width: initial;
  opacity: 0;
  overflow: hidden;
  text-align: left;
  transform: translateY(24px);
  transition:
    transform ${timingSlow} ease,
    opacity ${timingSlow} ease;
  width: 100%;

  &[data-enter] {
    opacity: 1;
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: opacity ${timingSlow} ease;
  }
`

export const Backdrop = styled(NextDialogBackdrop)`
  align-items: flex-end;
  background-color: transparent;
  justify-content: center;
  overflow: hidden;
  padding: 0;
  z-index: ${zIndexModal + 1};

  ${fromMd} {
    align-items: center;
  }
`

export const DialogContainer = styled.div`
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  overflow: hidden;
  padding: 0;
  position: fixed;
  z-index: ${zIndexModal};
`

export const DialogBody = styled.div`
  height: 100%;
  padding: 0;

  ${untilMd} {
    overflow-y: auto;
  }

  ${fromMd} {
    overflow-y: hidden;
  }
`

export const Layout = styled.div`
  display: flex;

  ${untilMd} {
    flex-direction: column;
    justify-content: space-between;
    min-height: 100%;
  }

  ${fromMd} {
    height: calc(100% - ${o => o.theme.spacing(16)});
    width: 100%;
  }
`

/**
 * One of the two halves of the dialog — the preview on the left, the options panel on the right.
 */
export const Half = styled.div`
  ${fromMd} {
    display: flex;
    min-height: 100vh;
  }

  &:first-of-type {
    flex: 1;
    flex-direction: column;
    position: relative;

    // On mobile the whole dialog scrolls. Letting the header out of this half keeps it stuck to
    // the top while the panel scrolls past
    ${untilMd} {
      display: contents;
    }
  }

  &:nth-of-type(2) {
    ${untilMd} {
      background-color: ${o => o.theme.color.fill.background};
      display: flex;
      flex: 1;
      z-index: 100;
    }

    ${fromMd} {
      flex: 0;
    }
  }
`

export const Panel = styled.div`
  background-color: ${o => o.theme.color.fill.background};
  display: flex;
  flex: 1;
  flex-direction: column;
  // Lets the panel shrink to the screen on mobile. Without it, a wide row inside, like the
  // swipeable icon strip, stretches the panel past the screen edge.
  min-width: 0;
  position: relative;

  ${fromMd} {
    border: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.subtle};
    overflow-y: auto;
    width: 420px;
  }
`

export const PanelBody = styled.div<{ $fillPanel?: boolean }>`
  ${untilMd} {
    padding: ${o => o.theme.spacing(0, 4)};
  }

  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(6)};
  padding: ${o => o.theme.spacing(10, 10, 5, 10)};

  ${o => o.$fillPanel !== false && 'flex: 1;'}
`

export const PreviewWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  ${untilMd} {
    flex: 1;
    justify-content: flex-start;
    position: relative;
  }

  ${fromMd} {
    inset: 0;
    position: absolute;
  }
`

export const Header = styled.header`
  display: flex;
  padding: ${o => o.theme.spacing(3, 0, 0, 10)};
  position: sticky;
  top: 0;
  z-index: ${o => o.theme.zIndex.foregroundContent + 1};

  ${untilMd} {
    background-color: ${o => o.theme.color.fill.background};
    padding: ${o => o.theme.spacing(1, 0)};
  }
`

export const HeaderSide = styled.div`
  flex: 1;

  &:last-child {
    display: flex;
    justify-content: flex-end;

    ${fromMd} {
      display: none;
    }
  }
`

export const HeaderTitleWrapper = styled.div`
  align-content: center;
  flex: 4;
  text-align: center;

  /* Desktop shows the title in the panel, but the dialog still needs its h1. */
  ${fromMd} {
    ${screenReaderOnly}
  }
`

export const HeaderTitle = styled.h1`
  ${TypeStyle.css.headlineExtraExtraSmall}
  text-transform: uppercase;
  ${o => o.theme.typography.effect.uppercase};
`
